<template>
  <Card class="@container/card">
    <CardHeader>
      <CardTitle>Cashback activity</CardTitle>
      <CardDescription>
        <span class="hidden @[540px]/card:block">User cashback earnings over time</span>
        <span class="@[540px]/card:hidden">User cashback earnings</span>
      </CardDescription>
      <CardAction>
        <div
          class="hidden @[767px]/card:flex items-center rounded-lg border p-0.5 text-sm"
        >
          <button
            v-for="opt in options"
            :key="opt.value"
            type="button"
            class="px-4 py-1 rounded-md transition-colors"
            :class="
              timeRange === opt.value
                ? 'bg-muted text-foreground'
                : 'text-muted-foreground hover:text-foreground'
            "
            @click="timeRange = opt.value"
          >
            {{ opt.label }}
          </button>
        </div>
        <Select v-model="timeRange">
          <SelectTrigger class="flex w-40 @[767px]/card:hidden" size="sm" aria-label="Select a value">
            <SelectValue placeholder="Last 7 days" />
          </SelectTrigger>
          <SelectContent class="rounded-xl">
            <SelectItem v-for="opt in options" :key="opt.value" :value="opt.value" class="rounded-lg">
              {{ opt.label }}
            </SelectItem>
          </SelectContent>
        </Select>
      </CardAction>
    </CardHeader>
    <CardContent class="px-2 sm:px-6">
      <div v-if="loading" class="h-62.5 w-full">
        <Skeleton class="h-full w-full" />
      </div>
      <div
        v-else-if="!hasActivity"
        class="flex h-62.5 w-full items-center justify-center text-sm text-muted-foreground"
      >
        No cashback activity in this period
      </div>
      <div v-else class="relative w-full" @mouseleave="hover = null">
        <div class="h-32 w-full">
          <svg
            ref="svg"
            :viewBox="`0 0 ${width} ${height}`"
            preserveAspectRatio="none"
            class="h-full w-full overflow-visible"
            @mousemove="onMove"
          >
            <defs>
              <linearGradient id="fillCashback" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stop-color="var(--primary)" stop-opacity="1" />
                <stop offset="95%" stop-color="var(--primary)" stop-opacity="0.1" />
              </linearGradient>
            </defs>

            <!-- horizontal grid lines -->
            <line
              v-for="(g, i) in gridLines"
              :key="`g${i}`"
              :x1="0"
              :x2="width"
              :y1="g"
              :y2="g"
              class="stroke-border/50"
              stroke-width="1"
              vector-effect="non-scaling-stroke"
            />

            <path :d="cashbackArea" fill="url(#fillCashback)" />
            <path :d="cashbackLine" fill="none" stroke="var(--primary)" stroke-width="1.5" vector-effect="non-scaling-stroke" />

            <line
              v-if="hover"
              :x1="hover.x"
              :x2="hover.x"
              :y1="0"
              :y2="height"
              class="stroke-border"
              stroke-width="1"
              vector-effect="non-scaling-stroke"
            />
          </svg>
        </div>

        <!-- x axis labels -->
        <div class="mt-3 flex justify-between text-xs text-muted-foreground">
          <span v-for="(t, i) in xTicks" :key="`t${i}`">{{ t }}</span>
        </div>

        <!-- tooltip -->
        <div
          v-if="hover"
          class="pointer-events-none absolute z-10 rounded-lg border bg-background px-2.5 py-1.5 text-xs shadow-md"
          :style="tooltipStyle"
        >
          <div class="mb-1 font-medium">{{ hover.label }}</div>
          <div class="flex items-center gap-1.5">
            <span class="inline-block size-2 rounded-[2px] bg-primary"></span>
            <span class="text-muted-foreground">Cashback</span>
            <span class="ml-auto pl-3 font-mono font-medium tabular-nums">£{{ hover.value }}</span>
          </div>
          <div class="flex items-center gap-1.5">
            <span class="inline-block size-2 rounded-[2px] bg-primary/40"></span>
            <span class="text-muted-foreground">Events</span>
            <span class="ml-auto pl-3 font-mono font-medium tabular-nums">{{ hover.count }}</span>
          </div>
        </div>
      </div>
    </CardContent>
  </Card>
</template>

<script>
import {
  Card,
  CardAction,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Skeleton,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@boogi/ui'

const WIDTH = 800
const HEIGHT = 250

export default {
  name: 'ChartAreaInteractive',
  components: {
    Card,
    CardAction,
    CardContent,
    CardDescription,
    CardHeader,
    CardTitle,
    Skeleton,
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
  },
  data() {
    return {
      width: WIDTH,
      height: HEIGHT,
      // Defaults to the shortest range: the primary use case is seeing current
      // performance, and it is the fastest to render.
      timeRange: '7d',
      hover: null,
      loading: true,
      // Daily buckets as returned by the API: { date, value, count }.
      series: [],
      options: [
        { value: '7d', label: 'Last 7 days' },
        { value: '30d', label: 'Last 30 days' },
        { value: '90d', label: 'Last 3 months' },
      ],
    }
  },
  computed: {
    rangeDays() {
      if (this.timeRange === '30d') return 30
      if (this.timeRange === '7d') return 7
      return 90
    },
    // The API returns a continuous daily series, already zero-filled and ordered.
    filteredData() {
      return this.series
    },
    // The series always spans the full range, so an all-zero result is what
    // "nothing happened" looks like rather than an empty array.
    hasActivity() {
      return this.series.some((d) => d.count > 0)
    },
    maxY() {
      return Math.max(...this.filteredData.map((d) => d.value), 1) * 1.1
    },
    points() {
      const data = this.filteredData
      const n = data.length
      if (!n) return []
      return data.map((d, i) => {
        const x = n === 1 ? 0 : (i / (n - 1)) * this.width
        const y = this.height - (d.value / this.maxY) * this.height
        return { x, y, ...d }
      })
    },
    cashbackLine() {
      return this.smoothPath(this.points.map((p) => [p.x, p.y]))
    },
    cashbackArea() {
      const line = this.smoothPath(this.points.map((p) => [p.x, p.y]))
      if (!line) return ''
      const last = this.points[this.points.length - 1]
      const first = this.points[0]
      return `${line} L${last.x},${this.height} L${first.x},${this.height} Z`
    },
    gridLines() {
      return [0.25, 0.5, 0.75].map((f) => f * this.height)
    },
    xTicks() {
      const data = this.filteredData
      if (!data.length) return []
      const count = Math.min(6, data.length)
      const ticks = []
      for (let i = 0; i < count; i++) {
        const idx = Math.round((i / (count - 1)) * (data.length - 1))
        ticks.push(this.formatDate(data[idx].date))
      }
      return ticks
    },
    tooltipStyle() {
      if (!this.hover) return {}
      const leftPct = (this.hover.x / this.width) * 100
      const clamped = Math.min(Math.max(leftPct, 8), 92)
      return { left: `${clamped}%`, top: '8px', transform: 'translateX(-50%)' }
    },
  },
  created() {
    this.fetchActivity()
  },
  watch: {
    // Each range is its own query now, so switching tabs has to refetch.
    timeRange() {
      this.fetchActivity()
    },
  },
  methods: {
    // Catmull-Rom spline converted to cubic bezier for smooth (natural-style) curves.
    //
    // Around a sharp spike the spline's control points can overshoot past the zero
    // baseline, which drew the curve below the x axis for values that are never
    // negative. A cubic bezier always stays inside the convex hull of its four control
    // points, so clamping the two control points to the plot area keeps the whole curve
    // within it — the smoothing is preserved everywhere it does not cause an overshoot.
    smoothPath(pts) {
      if (!pts.length) return ''
      if (pts.length < 3) {
        return pts.map((p, i) => `${i === 0 ? 'M' : 'L'}${p[0]},${p[1]}`).join(' ')
      }
      // y grows downwards: 0 is the top of the plot, this.height is the zero baseline.
      const clampY = (y) => Math.min(Math.max(y, 0), this.height)
      let d = `M${pts[0][0]},${pts[0][1]}`
      for (let i = 0; i < pts.length - 1; i++) {
        const p0 = pts[i - 1] || pts[i]
        const p1 = pts[i]
        const p2 = pts[i + 1]
        const p3 = pts[i + 2] || p2
        const c1x = p1[0] + (p2[0] - p0[0]) / 6
        const c1y = clampY(p1[1] + (p2[1] - p0[1]) / 6)
        const c2x = p2[0] - (p3[0] - p1[0]) / 6
        const c2y = clampY(p2[1] - (p3[1] - p1[1]) / 6)
        d += ` C${c1x},${c1y} ${c2x},${c2y} ${p2[0]},${p2[1]}`
      }
      return d
    },
    formatDate(value) {
      return new Date(value).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
    },
    money(v) {
      return Number(v || 0).toLocaleString(undefined, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })
    },
    onMove(e) {
      const svg = this.$refs.svg
      if (!svg || !this.points.length) return
      const rect = svg.getBoundingClientRect()
      const ratio = (e.clientX - rect.left) / rect.width
      const xVal = ratio * this.width
      let nearest = this.points[0]
      let best = Infinity
      for (const p of this.points) {
        const dist = Math.abs(p.x - xVal)
        if (dist < best) {
          best = dist
          nearest = p
        }
      }
      this.hover = {
        x: nearest.x,
        label: this.formatDate(nearest.date),
        value: this.money(nearest.value),
        count: nearest.count,
      }
    },
    // Aggregated server side: paging the raw ledger truncated the series once a
    // publisher passed a few hundred events, and shipped every document to do it.
    async fetchActivity() {
      const requestedRange = this.timeRange
      this.loading = true
      this.hover = null
      try {
        const apiUrl = localStorage.getItem('apiUrl') || process.env.VUE_APP_API_URL
        const token = localStorage.getItem('token')
        const statuses = ['CREATED', 'BEING_VERIFIED', 'VERIFIED', 'RECEIVED', 'PAID', 'DECLINED', 'REFUNDED']
        // Bucket by the viewer's own days rather than UTC, so "today" matches their calendar.
        const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC'
        const res = await this.$apiFetch(
          `${apiUrl}/private/api/client/cashback-ledger/activity?days=${this.rangeDays}` +
            `&timezone=${encodeURIComponent(timezone)}&status=${statuses}`,
          {
            method: 'GET',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `${token}`,
            },
          }
        )
        // A slower earlier request must not overwrite a newer range's data.
        if (requestedRange !== this.timeRange) return
        const data = res.ok ? await res.json() : []
        // The endpoint returns an array of daily buckets; coerce anything else to
        // an empty series so a malformed response can't break the chart's computeds.
        this.series = Array.isArray(data) ? data : []
      } catch (e) {
        if (requestedRange === this.timeRange) this.series = []
      } finally {
        if (requestedRange === this.timeRange) this.loading = false
      }
    },
  },
}
</script>
